// Helpers for showing assistant credits (GET /api/ai/usage/), shared by the
// chat page's usage ring and the account menu's credit section.
//
// The examples below use this usage response for a verified user:
//   { plan: { code: 'free', name: 'Free' }, available: true, unavailable_message: null,
//     global_limit_reached: false,
//     limits: [{ period: 'week', used: 410, limit: 500, remaining: 90, resets_at: '2026-10-05T00:00:00Z' }] }
import { ChatUsage, ChatUsageLimit, ChatUsagePeriod } from '../types/api';

// Usage at or above this share of a limit is shown in the warning colour.
export const USAGE_WARNING_RATIO = 0.8;

/** For sentences: `${PERIOD_CREDITS.week} assistant credits` -> "this week's assistant credits". */
export const PERIOD_CREDITS: Record<ChatUsagePeriod, string> = {
  week: "this week's",
  month: "this month's",
  year: "this year's",
  total: 'your',
};

/** Row labels: PERIOD_LABELS.total -> "All time". */
export const PERIOD_LABELS: Record<ChatUsagePeriod, string> = {
  week: 'This week',
  month: 'This month',
  year: 'This year',
  total: 'All time',
};

/**
 * Share of the limit used, 0–1 (a 0 limit counts as fully used).
 * e.g. 410 of 500 -> 0.82; 515 of 500 (the last message overshot) -> 1; 0 of 0 -> 1.
 */
export function usedRatio(limit: ChatUsageLimit): number {
  return limit.limit > 0 ? Math.min(limit.used / limit.limit, 1) : 1;
}

/** At most one decimal, in the viewer's locale: 483.728 -> "483.7", 1500 -> "1,500". */
export function formatCredits(credits: number): string {
  return credits.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/**
 * A reset time in the viewer's time zone, or null for the "total" period (it never resets).
 * e.g. '2026-10-05T00:00:00Z' -> "Mon, Oct 5, 5:30 AM" in India.
 */
export function formatReset(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The limit closest to running out: the one worth showing.
 * e.g. week 410/500 (82%) and month 900/2000 (45%) -> the week.
 */
export function tightestLimit(usage: ChatUsage | undefined): ChatUsageLimit | null {
  return (usage?.limits ?? []).reduce<ChatUsageLimit | null>(
    (tightest, limit) => (!tightest || usedRatio(limit) > usedRatio(tightest) ? limit : tightest),
    null
  );
}

/**
 * Why the user can't send a message right now, or null if they can. Examples:
 *   unverified email  -> "Verify your email address to use the assistant." (from the plan in chat_usage.json)
 *   global limit hit  -> "The assistant has reached its overall usage limit and is paused for now."
 *   week used up      -> "You've used all of this week's assistant credits. They reset Mon, Oct 5, 5:30 AM."
 *   the example above -> null (90 credits left)
 */
export function usageBlockedReason(usage: ChatUsage | undefined): string | null {
  if (!usage) return null;
  if (!usage.available) return usage.unavailable_message ?? "The assistant isn't available on your plan.";
  if (usage.global_limit_reached) {
    return 'The assistant has reached its overall usage limit and is paused for now.';
  }
  const tightest = tightestLimit(usage);
  if (tightest && tightest.remaining <= 0) {
    const reset = formatReset(tightest.resets_at);
    return `You've used all of ${PERIOD_CREDITS[tightest.period]} assistant credits.${reset ? ` They reset ${reset}.` : ''}`;
  }
  return null;
}
